





















































import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import moment from 'moment';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { BrandingHelper } from '@getaccept/lib-shared/src/signing-theme/helpers/branding.helper';
import { BrowserHelper } from '@getaccept/lib-shared/src/chat/helpers/browser.helper';
import type { SigningPageDocument as IDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { setMomentLocale } from '@getaccept/lib-shared/src/moment';
import type { Entity as IEntity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import { Toasts } from '@getaccept/lib-shared/src/toast';
import type { AuthenticationStep } from './authentication/types/authentication-step';
import type { AuthenticationStepInput } from './authentication/types/authentication-step-input';
import { Document } from './documents/store/document.constants';
import { BackgroundType } from './entity/types/enums/background-type';
import { loadLanguageAsync, userLanguage } from './lang';
import Lobby from './pages/Lobby.vue';
import UniversalLinkLobby from './universal-link/components/UniversalLinkLobby.vue';
import type { DocumentState } from './documents/store/document.types';
import { Entity } from './entity/store/entity.constants';
import type { EntityState } from './entity/store/entity.types';
import { EntityAction } from './entity/store/entity.types';
import { Session } from './session/store/session.constants';
import type { SessionState } from './session/store/session.types';
import { SessionAction, SessionGetter } from './session/store/session.types';
import type { RootState } from './store/root.types';
import { RootAction } from './store/root.types';
import { Theme } from './theme/store/theme.constants';
import type { ThemeState } from './theme/store/theme.types';
import { ThemeAction } from './theme/store/theme.types';
import { pusherClient } from './pusher';
import { DocumentHelper } from './helpers/document.helper';

@Component({
  components: {
    UniversalLinkLobby,
    Lobby,
    Toasts,
  },
})
export default class App extends Vue {
  @State((state: RootState) => state.authId) authId: string;
  @State((state: RootState) => state.documentId) documentId: string;
  @State((state: RootState) => state.recipientId) recipientId: string;
  @State((state: RootState) => state.userId) userId: string;
  @State((state: RootState) => state.isPreview) isPreview: boolean;
  @State((state: RootState) => state.previewExpired) previewExpired: boolean;
  @Action(RootAction.SetPusherConnected) actionSetPusherConnected: (connected: boolean) => void;

  @Session.State((state: SessionState) => state.token) token: { jwt: string; exp: number };
  @Session.State((state: SessionState) => state.recipientFirstOpen) recipientFirstOpen: boolean;
  @Session.State((state: SessionState) => state.isCorrectPin) isCorrectPin: boolean;
  @Session.State((state: SessionState) => state.status) status: DocumentStatus;
  @Session.State((state: SessionState) => state.error) sessionError: boolean;
  @Session.State((state: SessionState) => state.authentication) authentication: {
    submittingResponse: boolean;
    steps: AuthenticationStep[];
    answers: AuthenticationStepInput[];
  };
  @Session.Getter(SessionGetter.NextAuthStep) nextAuthStep: AuthenticationStep;
  @Session.Getter(SessionGetter.DocumentRecalled) documentRecalled: boolean;
  @Session.Getter(SessionGetter.ConsentSteps) consentSteps: AuthenticationStep;
  @Session.Getter(SessionGetter.GDPRDeclined) gdprDeclined: boolean;
  @Session.Action(SessionAction.LoadToken) actionLoadToken: (
    authStepsInput?: AuthenticationStepInput[]
  ) => Promise<void>;
  @Session.Action(SessionAction.LoadTokenSuccess) setToken: (payload: {
    token: {
      jwt: string;
      exp: number;
    };
    authStepsInput: AuthenticationStepInput[];
  }) => void;
  @Session.Action(SessionAction.SubmitAnswer) actionSubmitAnswer: (
    authStepsInput: AuthenticationStepInput[]
  ) => Promise<void>;
  @Session.Action(SessionAction.SendSmsCode) actionSendSmsCode: () => Promise<void>;
  @Session.Action(SessionAction.SubmitSmsAnswer) actionSubmitSmsAnswer: (
    authStepsInput: AuthenticationStepInput
  ) => Promise<void>;
  @Session.Action(SessionAction.SubmitQnaAnswer) actionSubmitQnaAnswer: (
    authStep: AuthenticationStepInput
  ) => Promise<void>;
  @Session.Action(SessionAction.SubmitConsent) actionSubmitConsent: (
    authStepsInput: AuthenticationStepInput[]
  ) => Promise<void>;
  @Session.Action(SessionAction.DeclineConsent) actionDeclineConsent: () => void;

  @Entity.State((state: EntityState) => state.entity) entity: IEntity;
  @Entity.Action(EntityAction.Load) actionLoadEntity: () => Promise<void>;

  @Theme.Action(ThemeAction.Load) actionLoadTheme: (documentId: string) => Promise<void>;
  @Theme.State((state: ThemeState) => state.theme) theme: SigningTheme;

  @Document.State((state: DocumentState) => state.document) document: IDocument;
  @Document.State((state: DocumentState) => state.error) documentError: boolean;

  showAlert = true;
  documentAvailable = false;

  constructor() {
    super();
  }

  @Watch('hasValidToken')
  watchToken() {
    if (this.hasValidToken) {
      this.loadEntity();
      this.loadPusher();
    }
  }

  get universalId(): string {
    return this.$route.params['universalId'];
  }

  get showUniversalLinkLobby(): boolean {
    const { universal } = this.$route.meta;
    return !!universal;
  }

  get showLobby(): boolean {
    return (
      !!this.nextAuthStep ||
      !!this.consentSteps ||
      this.hasError ||
      this.documentRecalled ||
      this.documentExpired ||
      this.documentRejected ||
      this.previewExpired ||
      this.gdprDeclined
    );
  }

  get pageTitle(): string {
    return this.document ? this.document.name : 'GetAccept';
  }

  get documentExpired(): boolean {
    return DocumentHelper.getIsDocumentExpired(this.document);
  }

  get hasFixedBackground(): boolean {
    return (
      !this.theme ||
      this.theme.backgroundType === BackgroundType.Fixed ||
      !this.theme.backgroundImage
    );
  }

  get documentRejected(): boolean {
    return !!this.document && this.document.status === DocumentStatus.Rejected;
  }

  get hasParams(): boolean {
    if (this.isPreview && this.documentId) {
      return true;
    }
    return !!this.authId && !!this.documentId && (!!this.recipientId || !!this.userId);
  }

  get hasError(): boolean {
    return this.sessionError || !this.hasParams || this.documentError;
  }

  get hasValidToken(): boolean {
    const notExpired: boolean =
      (this.token.exp && !moment().isAfter(moment.unix(this.token.exp))) || !this.token.exp;
    const tokenFound = !!this.token.jwt;
    return tokenFound && notExpired;
  }

  get safeBackground(): string {
    return BrandingHelper.getBackground(this.theme, BrowserHelper.isIE(navigator));
  }

  get isDsr() {
    return this.$route.path.includes('/dsr/');
  }

  get showDsr() {
    return !this.showUniversalLinkLobby && !this.showLobby && this.isDsr;
  }

  private static unhandledrejectionHandler(event) {
    if (bugsnagClient) {
      bugsnagClient.notify(event);
    }
  }

  created() {
    window.addEventListener('unhandledrejection', App.unhandledrejectionHandler);
  }

  beforeDestroy() {
    window.removeEventListener('unhandledrejection', App.unhandledrejectionHandler);
  }

  setDocumentTitle() {
    window.document.title = this.pageTitle;
  }

  async loadEntity() {
    await this.actionLoadEntity();
    if (!this.documentRecalled) {
      this.documentAvailable = true;
    }
  }

  async loadPusher() {
    try {
      await pusherClient.connect(this.token.jwt);
      this.actionSetPusherConnected(true);
    } catch (e) {
      console.error(e);
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }
  }

  async mounted() {
    loadLanguageAsync(userLanguage());
    setMomentLocale(userLanguage());
    if (this.showUniversalLinkLobby) {
      return;
    }
    if (this.hasParams) {
      this.actionLoadTheme(this.documentId);
    }
    if (!this.hasValidToken && this.hasParams) {
      await this.actionLoadToken([]);
    }
    if (this.hasValidToken && this.hasParams && !this.showLobby) {
      this.loadEntity();
      this.loadPusher();
    }
    this.setDocumentTitle();
  }
}
